import React, { useEffect } from 'react';
import { X, type LucideIcon } from 'lucide-react';

export interface StandardModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    icon?: LucideIcon;
    iconColor?: string;
    iconBgColor?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
    maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
    maxHeight?: string;
    zIndex?: string;
}

export const StandardModal: React.FC<StandardModalProps> = ({
    isOpen,
    onClose,
    title,
    subtitle,
    icon: Icon,
    iconColor = 'text-indigo-600',
    iconBgColor = 'bg-indigo-100',
    children,
    footer,
    maxWidth = 'xl',
    maxHeight = '',
    zIndex = 'z-[60]'
}) => {
    // Lock body scroll and handle Esc key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            window.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            window.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const maxWidthClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '3xl': 'max-w-3xl',
    }[maxWidth];

    const hasHeightConstraint = !!maxHeight;

    return (
        <div
            className={`fixed inset-0 bg-slate-900/70 backdrop-blur-sm ${zIndex} flex items-center justify-center p-4 animate-in fade-in duration-200`}
            onClick={onClose}
            role="dialog"
            aria-modal="true"
        >
            <div
                className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidthClasses} ${maxHeight} flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-200 border border-slate-100`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header Padronizado */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-violet-50 shrink-0 rounded-t-2xl">
                    <div className="flex items-center gap-3 min-w-0 pr-2">
                        {Icon && (
                            <div className={`${iconBgColor} p-2 rounded-xl shrink-0 shadow-sm`}>
                                <Icon size={20} className={iconColor} />
                            </div>
                        )}
                        <div className="min-w-0">
                            <h2 className="text-base md:text-lg font-bold text-slate-800 truncate leading-snug">
                                {title}
                            </h2>
                            {subtitle && (
                                <p className="text-xs text-slate-500 truncate mt-0.5 leading-snug">
                                    {subtitle}
                                </p>
                            )}
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-indigo-100/70 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 shrink-0"
                        aria-label="Fechar"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className={`p-5 md:p-6 bg-white ${hasHeightConstraint ? 'overflow-y-auto flex-1 min-h-0' : ''}`}>
                    {children}
                </div>

                {/* Footer Opcional */}
                {footer && (
                    <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3 shrink-0 rounded-b-2xl">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};
